import { useState, useCallback } from 'react';
import TabContainerHOC from './hocs/TabContainerHOC';
import Notification from '../global/Notification';
import StickySaveBar from '../global/StickySaveBar';
import { PageProps } from '../../types';
import { useAuth } from '../../utils/authorization';
import { useSideBySide } from './hooks/useSideBySide';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import {
    SectionConfig,
    CardConfigPanel,
    ImageConfigModal,
    LivePreview,
    PreviewModal,
} from './components/sideBySide';

const Description = () => (
    <>
        <p>
            Configure two highlight cards to be displayed <b>side-by-side</b> on the homepage.
        </p>
        <ul>
            <li>
                Upload images, set titles, subtitles, and <b>call-to-action buttons</b> for each card.
            </li>
            <li>
                Customize <b>colors</b> for each card.
            </li>
        </ul>
    </>
);

export default function SideBySidePage({ model }: PageProps) {
    const { user } = useAuth();
    const {
        data,
        loading,
        hasChanges,
        notification,
        saveError,
        updateSection,
        updateCard,
        swapCards,
        saveData,
        cancelChanges,
        clearNotification,
        clearSaveError,
        uploading1,
        uploading2,
        handleImageUpload,
    } = useSideBySide(model.storeId);

    const [showPreview, setShowPreview] = useState<1 | 2 | null>(null);

    const [imageConfigCard, setImageConfigCard] = useState<1 | 2 | null>(null);

    const handleSave = useCallback(async (): Promise<boolean> => {
        try {
            const success = await saveData();
            return success;
        } catch (error) {
            console.error("Error saving Side By Side:", error);
            return false;
        }
    }, [saveData, user?.email]);

    const unsavedChangesModal = useUnsavedChangesGuard({ hasChanges, save: handleSave });

    const handleRemoveImage = useCallback((cardNum: 1 | 2) => {
        updateCard(cardNum, 'image', '');
    }, [updateCard]);

    const handleClosePreview = useCallback(() => {
        setShowPreview(null);
    }, []);

    const handleCloseImageConfig = useCallback(() => {
        setImageConfigCard(null);
    }, []);

    return (
        <>
            <TabContainerHOC
                title="Side By Side"
                description={<Description />}
                containerClass="side-by-side"
                loading={loading}
            >
                <SectionConfig
                    title={data.sectionTitle}
                    titleColor={data.sectionTitleColor}
                    subtitle={data.sectionSubtitle}
                    subtitleColor={data.sectionSubtitleColor}
                    onTitleChange={(value) => updateSection('sectionTitle', value)}
                    onTitleColorChange={(value) => updateSection('sectionTitleColor', value)}
                    onSubtitleChange={(value) => updateSection('sectionSubtitle', value)}
                    onSubtitleColorChange={(value) => updateSection('sectionSubtitleColor', value)}
                    disabled={loading}
                />

                <div className="cards-configuration">
                    <CardConfigPanel
                        cardNum={1}
                        data={data.card1}
                        onChange={updateCard}
                        onImageUpload={handleImageUpload}
                        uploading={uploading1}
                        onRemoveImage={handleRemoveImage}
                        onOpenImageConfig={setImageConfigCard}
                        onOpenPreview={() => setShowPreview(1)}
                        disabled={loading}
                    />

                    <CardConfigPanel
                        cardNum={2}
                        data={data.card2}
                        onChange={updateCard}
                        onImageUpload={handleImageUpload}
                        uploading={uploading2}
                        onRemoveImage={handleRemoveImage}
                        onOpenImageConfig={setImageConfigCard}
                        onOpenPreview={() => setShowPreview(2)}
                        disabled={loading}
                    />
                </div>

                <LivePreview data={data} onSwap={swapCards} />

                <PreviewModal
                    data={data}
                    isOpen={showPreview !== null}
                    activeCard={showPreview}
                    onClose={handleClosePreview}
                    onSwap={swapCards}
                />

                <ImageConfigModal
                    cardNum={imageConfigCard}
                    data={imageConfigCard === 1 ? data.card1 : imageConfigCard === 2 ? data.card2 : null}
                    onChange={updateCard}
                    onClose={handleCloseImageConfig}
                />

                {unsavedChangesModal}
            </TabContainerHOC>
            <StickySaveBar
                visible={hasChanges}
                saving={loading}
                disabled={uploading1 || uploading2}
                onSave={handleSave}
                onCancel={cancelChanges}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={clearSaveError}
            />
            <Notification message={notification.message} type={notification.type} onClose={clearNotification} />
        </>
    );
}